import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle2, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "Partnership", k: "Partnership" },
  { value: "Investment", k: "Investment" },
  { value: "Research Collaboration", k: "ResearchCollaboration" },
  { value: "Government/Defense", k: "GovernmentDefense" },
  { value: "Media", k: "Media" },
  { value: "General", k: "General" },
];

export default function ContactSection() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", organization: "", inquiry_type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch (err) {
      setError(t("contact.form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-[#9CA3AF] font-mono mb-4">{t("contact.label")}</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-[#F3F4F6] tracking-tighter leading-none mb-8">
              {t("contact.heading")}
            </h2>
            <p className="text-base text-[#9CA3AF] leading-relaxed mb-10 max-w-md">
              {t("contact.body")}
            </p>

            <div className="space-y-4">
              {[
                { label: t("contact.partnerships"), href: "mailto:info@rolinkbiotechnology.com" },
                { label: t("contact.investors"),    href: "mailto:info@rolinkbiotechnology.com" },
                { label: t("contact.general"),      href: "mailto:info@rolinkbiotechnology.com" },
              ].map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  className="flex items-center justify-between py-3 border-b border-[#1F2937] text-[#9CA3AF] hover:text-[#EE1D25] transition-colors group"
                >
                  <span className="text-sm">{link.label}</span>
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-[#1F2937]">
              <p className="text-xs text-[#4B5563] leading-relaxed">
                {t("contact.orgFooter")}
              </p>
              <p className="text-xs text-[#4B5563] mt-1">{t("contact.addr")}</p>
              <p className="text-xs text-[#4B5563] mt-1">info@rolinkbiotechnology.com</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="contact-success">
                <CheckCircle2 size={48} className="text-[#10B981] mb-4" />
                <h3 className="font-heading text-2xl font-bold text-[#F3F4F6] mb-2">{t("contact.form.successT")}</h3>
                <p className="text-sm text-[#9CA3AF]">{t("contact.form.successB")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{t("contact.form.name")}</Label>
                    <Input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={e => set("name", e.target.value)}
                      required
                      className="mt-1.5 bg-[#111111] border-[#1F2937] text-white rounded-none h-11 focus:border-[#EE1D25]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{t("contact.form.email")}</Label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={e => set("email", e.target.value)}
                      required
                      className="mt-1.5 bg-[#111111] border-[#1F2937] text-white rounded-none h-11 focus:border-[#EE1D25]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{t("contact.form.org")}</Label>
                  <Input
                    data-testid="contact-org-input"
                    value={form.organization}
                    onChange={e => set("organization", e.target.value)}
                    className="mt-1.5 bg-[#111111] border-[#1F2937] text-white rounded-none h-11 focus:border-[#EE1D25]"
                  />
                </div>

                <div>
                  <Label className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{t("contact.form.type")}</Label>
                  <Select value={form.inquiry_type} onValueChange={v => set("inquiry_type", v)} required>
                    <SelectTrigger data-testid="contact-type-select" className="mt-1.5 bg-[#111111] border-[#1F2937] text-white rounded-none h-11">
                      <SelectValue placeholder={t("contact.form.typePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border-[#1F2937]">
                      {INQUIRY_TYPES.map(it => (
                        <SelectItem key={it.value} value={it.value} className="text-[#F3F4F6] hover:bg-[#1F2937]">{t(`contact.form.types.${it.k}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-[#9CA3AF] uppercase tracking-[0.15em]">{t("contact.form.message")}</Label>
                  <Textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={e => set("message", e.target.value)}
                    required
                    rows={4}
                    className="mt-1.5 bg-[#111111] border-[#1F2937] text-white rounded-none resize-none focus:border-[#EE1D25]"
                  />
                </div>

                {error && <p className="text-sm text-red-400">{error}</p>}

                <Button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#EE1D25] hover:bg-[#CC1920] !text-white h-12 rounded-none font-medium"
                >
                  {loading ? t("contact.form.submitting") : <><Send size={16} className="mr-2" /> {t("contact.form.submit")}</>}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
